import { forwardRef } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import styled, { css } from 'styled-components';
import Card from 'components/generic/card';
import PageAction from 'components/shared/Routing/PageAction';
import { outline, removeButtonStyling } from 'modules/helpers/mixins';
import ThemeProvider from 'theming/ThemeProvider';
import ActionsAndLink from './components/ActionsAndLink';
import ContentCardLayout from './components/ContentCardLayout';
import ImageWrapper from './components/ImageWrapper';
import type { ContentCardProps, ImageAspectRatio } from './types';

// #region Styles
const ActionWrapperStyling = css`
&:after {
        content: '';
        position: absolute;
        top: 0;     
        left: 0;
        width: 100%;
        height: 100%;
        inset: 0;
        z-index: 1;
    }

    &:focus-visible:after {
        ${outline}
        border-radius: ${({ theme }) => theme.RadiusMedium};
    }
`;

const StyledPageAction = styled(PageAction)`
    ${ActionWrapperStyling}
`;

const ButtonWrapper = styled.button`
    ${removeButtonStyling}
    ${ActionWrapperStyling}
`;

const StyledCard = styled(Card)<{ $showBorder: boolean; $isTransparent?: boolean }>`
    position: relative;
    height: 100%;
    ${({ $isTransparent }) =>
        $isTransparent
            ? css`
            background-color: transparent;
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        `
            : css`
        background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
    `}

    ${({ $showBorder, theme }) =>
        $showBorder &&
        css`
            border: 1px solid ${theme.ColorBorderTertiary};
    `};
`;

const StyledContentWrapper = styled.div<{ $isTransparent?: boolean; $aspectRatio?: ImageAspectRatio }>`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing4};
    padding: ${({ theme }) => `${theme.Spacing16} ${theme.Spacing16} ${theme.Spacing24} ${theme.Spacing16}`};

    ${({ $isTransparent }) =>
        $isTransparent &&
        css`
            padding-left: 0;
            padding-bottom: ${({ theme }) => theme.Spacing2}; // for the underline on the heading when focused or hovered
    `};

    ${({ $aspectRatio }) =>
        $aspectRatio === 'fill'
            ? css`
            flex: 0 0 auto;
        `
            : css`
            flex: 1 0 0%;
        `}

    ${StyledPageAction}:is(:focus-visible, :active, :hover, :hover:focus) ~ & h3,
    ${ButtonWrapper}:is(:focus-visible, :active, :hover, :hover:focus) ~ & h3 {
        text-decoration: underline;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
    }
`;

const StyledImageWrapper = styled(ImageWrapper)`
    ${StyledPageAction}:is(:focus-visible, :active, :hover, :hover:focus) ~ &:before,
    ${ButtonWrapper}:is(:focus-visible, :active, :hover, :hover:focus) ~ &:before {
        // hover overlay effect
        content: '';
        position: absolute;
        background-color: rgba(255, 255, 255, 0.1);
        height: 100%;
        width: 100%;
    }
`;
// #endregion Styles

const ContentCard = forwardRef<HTMLDivElement, ContentCardProps>((props, ref) => {
    const {
        type,
        image,
        imageSecondary,
        heading,
        subheading,
        tags,
        link,
        actions,
        children,
        cardProps,
        onClickWrapper,
        splashText,
        showPlayIcon = false,
        isTransparent = false,
        showBorder = false,
        splashRenderer,
        ...restProps
    } = props;
    const hasOnClickWrapper = onClickWrapper !== undefined;
    const showContentSection = heading || subheading || children || actions || link?.label;

    return (
        <StyledCard
            $showBorder={showBorder && !isTransparent}
            $isTransparent={isTransparent}
            forwardedAs="article"
            ref={ref}
            {...restProps}
            {...cardProps}
        >
            {link?.action && !hasOnClickWrapper && (
                <StyledPageAction action={link.action} aria-label={link.label || heading} />
            )}
            {hasOnClickWrapper && <ButtonWrapper onClick={onClickWrapper} />}
            <StyledImageWrapper
                image={image}
                imageSecondary={imageSecondary}
                tags={tags}
                splashText={splashText}
                showPlayIcon={showPlayIcon}
                isTransparent={isTransparent}
                splashRenderer={splashRenderer}
            />
            {showContentSection && (
                <StyledContentWrapper $isTransparent={isTransparent} $aspectRatio={image?.aspectRatio}>
                    <ContentCardLayout subheading={subheading} heading={heading}>
                        {children}
                    </ContentCardLayout>
                    <ActionsAndLink link={link} actions={actions} />
                </StyledContentWrapper>
            )}
        </StyledCard>
    );
});

const ContentCardThemeWrapper = forwardRef<HTMLDivElement, ContentCardProps>((props, ref) => {
    const { type, isTransparent = false } = props;
    const theme = isTransparent ? 'default' : type;
    const showBorder = props.showBorder !== undefined ? props.showBorder : theme === 'default';

    return theme ? (
        <ThemeProvider theme={theme}>
            <ContentCard ref={ref} {...props} showBorder={showBorder} />
        </ThemeProvider>
    ) : (
        // if no type is provided we will use the global theme
        <ContentCard ref={ref} {...props} showBorder={showBorder} />
    );
});

export default withErrorBoundary(ContentCardThemeWrapper, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
